import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import Layout from '../../components/Layout';
import './SurveyResults.css';
import config from '../../config';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const SurveyResults = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartTypes, setChartTypes] = useState({});
  const [selectedPersonas, setSelectedPersonas] = useState([]);
  const [availablePersonas, setAvailablePersonas] = useState([]);
  const [enlargedAttachment, setEnlargedAttachment] = useState(null);
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const wsRef = useRef(null);
  const reportStatusRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const shouldReconnectRef = useRef(true);

  // Helper function to get full image URL
  const getAttachmentUrl = (attachment) => {
    // If it's already a data URL (base64), return as-is
    if (attachment.content && attachment.content.startsWith('data:')) {
      return attachment.content;
    }

    // If URL exists, ensure it's a full URL
    if (attachment.url) {
      // If it already starts with http, return as-is
      if (attachment.url.startsWith('http')) {
        return attachment.url;
      }
      // Otherwise, prepend the base URL
      return `${config.api.baseUrl}${attachment.url}`;
    }

    // Fallback to content field
    return attachment.content || '';
  };

  useEffect(() => {
    shouldReconnectRef.current = true;
    reconnectAttemptsRef.current = 0;
    fetchResults();

    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Extract unique personas from results
  useEffect(() => {
    if (results && Object.keys(results).length > 0) {
      const personasSet = new Set();
      Object.values(results).forEach(result => {
        result.responses?.forEach(resp => {
          personasSet.add(JSON.stringify({
            id: String(resp.persona_id), // Ensure string format
            name: resp.persona_name
          }));
        });
      });
      const personas = Array.from(personasSet).map(p => JSON.parse(p));
      setAvailablePersonas(personas);
      // Start with empty selection (show all data)
      setSelectedPersonas([]);
    }
  }, [results]);

  useEffect(() => {
    const getCurrentTheme = () => {
      const rootTheme = document.documentElement?.getAttribute('data-theme');
      const bodyTheme = document.body?.getAttribute('data-theme');
      return (rootTheme || bodyTheme || '').toLowerCase();
    };

    const syncTheme = () => {
      setIsDarkTheme(getCurrentTheme() === 'dark');
    };

    syncTheme();

    const observer = new MutationObserver(syncTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    reportStatusRef.current = report?.status || null;
  }, [report]);

  // Keep qualitative scroll regions aligned with the tallest card in each grid row.
  useEffect(() => {
    const adjustQualitativeScrollHeights = () => {
      const container = document.querySelector('.results-container');
      if (!container) return;

      const cards = Array.from(container.querySelectorAll('.question-results'));
      if (cards.length === 0) return;

      // Group cards by visual row using top position
      const rows = new Map();
      cards.forEach((card) => {
        const top = Math.round(card.getBoundingClientRect().top);
        if (!rows.has(top)) rows.set(top, []);
        rows.get(top).push(card);
      });

      rows.forEach((rowCards) => {
        const rowMaxHeight = Math.max(...rowCards.map((card) => card.getBoundingClientRect().height));

        rowCards.forEach((card) => {
          if (!card.classList.contains('question-results--qualitative')) return;

          const scrollRegion = card.querySelector('.qualitative-scroll-region');
          if (!scrollRegion) return;

          const cardHeight = card.getBoundingClientRect().height;
          const regionHeight = scrollRegion.getBoundingClientRect().height;
          const nonScrollableHeight = cardHeight - regionHeight;
          const availableHeight = Math.max(500, Math.floor(rowMaxHeight - nonScrollableHeight));

          scrollRegion.style.maxHeight = `${availableHeight}px`;
        });
      });
    };

    const rafId = requestAnimationFrame(adjustQualitativeScrollHeights);
    window.addEventListener('resize', adjustQualitativeScrollHeights);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', adjustQualitativeScrollHeights);
    };
  }, [results, selectedPersonas, report]);

  const fetchResults = async () => {
    try {
      setError(null);
      const res = await fetch(`${config.api.baseUrl}/synthetic-audience/reports/${id}/results`, {
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to fetch results');

      const data = await res.json();
      setReport(data.report);
      setResults(data.results);

      // If report is running, start WebSocket stream
      if (data.report.status === 'running') {
        reconnectAttemptsRef.current = 0;
        startWebSocket();
      } else {
        stopWebSocket();
      }
    } catch (err) {
      setError('Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const startWebSocket = () => {
    // Don't create duplicate connections
    if (wsRef.current) {
      return;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Create WebSocket URL (convert http to ws)
    const baseUrl = config.api.baseUrl;
    let wsUrl = baseUrl.replace(/^http/, 'ws');
    wsUrl += `/synthetic-audience/reports/stream`;

    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      reconnectAttemptsRef.current = 0;
      // Send init message with report_id
      ws.send(JSON.stringify({
        type: 'init',
        report_id: id
      }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Handle errors from server
        if (data.type === 'error') {
          setError(data.message);
          stopWebSocket();
          return;
        }

        // Handle progress updates
        if (data.type === 'progress') {
          setError(null);
          // Update report progress in real-time
          setReport(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              status: data.status,
              progress: data.progress,
              completed_at: data.completed_at || prev.completed_at
            };
          });

          // Keep results fetch as a single API call on page load.
          // For live progress, rely on websocket status only.
          if (data.status === 'completed' || data.status === 'failed') {
            shouldReconnectRef.current = false;
            stopWebSocket();
          }
        }
      } catch (err) {
        // Silently handle parsing errors
      }
    };

    ws.onerror = () => {
      stopWebSocket();
    };

    ws.onclose = (event) => {
      wsRef.current = null;

      const reportStillRunning = reportStatusRef.current === 'running';
      const closedNormally = event.code === 1000;
      if (!shouldReconnectRef.current || !reportStillRunning || closedNormally) {
        return;
      }

      // Exponential backoff reconnect for transient disconnects.
      reconnectAttemptsRef.current += 1;
      const backoffMs = Math.min(15000, 1000 * (2 ** (reconnectAttemptsRef.current - 1)));
      reconnectTimeoutRef.current = setTimeout(() => {
        if (shouldReconnectRef.current && !wsRef.current) {
          startWebSocket();
        }
      }, backoffMs);
    };

    wsRef.current = ws;
  };

  const stopWebSocket = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  };

  const handleExport = async () => {
    try {
      const response = await fetch(
        `${config.api.baseUrl}/synthetic-audience/reports/${id}/results/export`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error('Failed to export results');
      }

      // Get the blob from the response
      const blob = await response.blob();

      // Create a download link and trigger it
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${id}_results.csv`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('Failed to export results. Please try again.');
    }
  };

  const truncateLabel = (label, maxLen = 28) => {
    if (!label || typeof label !== 'string') return label;
    return label.length > maxLen ? `${label.slice(0, maxLen - 1)}…` : label;
  };

  const getChartData = (aggregated, chartType, question) => {
    // Build complete list of all possible options based on question type
    let allOptions = [];

    if (question.type === 'yes_no') {
      // Yes/No always has these two options
      allOptions = ['Yes', 'No'];
    } else if (question.type === 'rating_scale') {
      // Rating scale shows all values from min to max
      const min = question.scale_min || 1;
      const max = question.scale_max || 5;
      for (let i = min; i <= max; i++) {
        allOptions.push(String(i));
      }
    } else if (question.type === 'multiple_choice' || question.type === 'multi_select') {
      // Multiple choice and multi-select use defined options
      allOptions = question.options || [];
    }

    // Create data array with 0 for options that have no responses
    const labels = allOptions;
    const data = allOptions.map(option => aggregated[option] || 0);

    const colors = chartType === 'bar'
      ? 'rgba(75, 192, 192, 0.8)'
      : [
          'rgba(255, 99, 132, 0.8)',
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 206, 86, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(153, 102, 255, 0.8)',
          'rgba(255, 159, 64, 0.8)',
          'rgba(255, 99, 255, 0.8)',
          'rgba(99, 255, 132, 0.8)',
          'rgba(201, 203, 207, 0.8)',
          'rgba(83, 102, 255, 0.8)'
        ];

    return {
      labels,
      datasets: [{
        label: chartType === 'bar' ? 'Responses' : '',
        data,
        backgroundColor: chartType === 'bar' ? colors : colors.slice(0, labels.length),
        borderColor: chartType === 'bar'
          ? 'rgba(75, 192, 192, 1)'
          : colors.slice(0, labels.length).map(c => c.replace('0.8', '1')),
        borderWidth: 1
      }]
    };
  };

  const chartTextColor = isDarkTheme ? '#e0e0e0' : '#2f3b4d';
  const chartGridColor = isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(34, 54, 81, 0.15)';

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          color: chartTextColor,
          font: { size: 12 },
          padding: 15
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#444',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed.y || context.parsed;
            const dataset = context.dataset;
            const total = dataset.data.reduce((a, b) => a + b, 0);
            const percent = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${label}: ${value} (${percent}%)`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: chartTextColor,
          stepSize: 1
        },
        grid: {
          color: chartGridColor
        }
      },
      x: {
        ticks: {
          color: chartTextColor,
          maxRotation: 0,
          minRotation: 0,
          callback: function(value) {
            const label = this.getLabelForValue(value);
            return truncateLabel(label, 24);
          }
        },
        grid: {
          color: chartGridColor
        }
      }
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          color: chartTextColor,
          font: { size: 12 },
          padding: 15
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#444',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed;
            const dataset = context.dataset;
            const total = dataset.data.reduce((a, b) => a + b, 0);
            const percent = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${label}: ${value} (${percent}%)`;
          }
        }
      }
    }
  };

  // Helper function to recompute aggregation from filtered responses
  const recomputeAggregation = (responses, questionType) => {
    if (!responses || responses.length === 0) return {};

    const aggregated = {};

    responses.forEach((resp, idx) => {
      if (questionType === 'qualitative') {
        // Qualitative doesn't aggregate
        return;
      }

      const response = resp.response;
      if (!response) {
        return;
      }

      if (questionType === 'multi_select' && response.selected_options) {
        // Multi-select: count each selected option
        response.selected_options.forEach(option => {
          aggregated[option] = (aggregated[option] || 0) + 1;
        });
      } else if (response.answer !== undefined) {
        // Single answer questions (multiple_choice, yes_no, rating_scale)
        const answer = String(response.answer);
        aggregated[answer] = (aggregated[answer] || 0) + 1;
      } else if (response.choice !== undefined) {
        // Alternative structure - choice instead of answer
        const choice = String(response.choice);
        aggregated[choice] = (aggregated[choice] || 0) + 1;
      } else if (response.rating !== undefined) {
        // Alternative structure - rating
        const rating = String(response.rating);
        aggregated[rating] = (aggregated[rating] || 0) + 1;
      } else if (response.choices !== undefined) {
        // Alternative structure - choices array for multi-select
        response.choices.forEach(choice => {
          aggregated[choice] = (aggregated[choice] || 0) + 1;
        });
      }
    });

    return aggregated;
  };

  // Persona filtering handlers
  const togglePersona = (personaId) => {
    setSelectedPersonas(prev =>
      prev.includes(personaId)
        ? prev.filter(id => id !== personaId)
        : [...prev, personaId]
    );
  };

  const selectAllPersonas = () => {
    const allIds = availablePersonas.map(p => p.id);
    setSelectedPersonas(allIds);
  };

  const clearAllPersonas = () => {
    // Clear all = show all data (empty selection)
    setSelectedPersonas([]);
  };

  // Filter results by selected personas
  const filteredResults = useMemo(() => {
    // If no personas selected, show all data
    if (!results || selectedPersonas.length === 0) return results;

    // If personas are selected, only show data for those personas
    const filtered = {};
    Object.entries(results).forEach(([questionId, result]) => {
      const filteredResponses = result.responses?.filter(r => {
        const responsePersonaId = String(r.persona_id).trim();
        const isIncluded = selectedPersonas.some(pid => String(pid).trim() === responsePersonaId);
        return isIncluded;
      }) || [];

      const aggregatedData = recomputeAggregation(filteredResponses, result.type);

      filtered[questionId] = {
        ...result,
        responses: filteredResponses,
        aggregated: aggregatedData,
        total_responses: filteredResponses.length
      };
    });
    return filtered;
  }, [results, selectedPersonas]);

  const renderQuantitativeResults = (questionId, result, question) => {
    const aggregated = result.aggregated || {};
    const total = result.total_responses || 0;
    const chartType = chartTypes[questionId] || 'bar';

    if (Object.keys(aggregated).length === 0) {
      return (
        <div className="no-responses">
          No responses yet for this question
        </div>
      );
    }

    const chartData = getChartData(aggregated, chartType, question);

    return (
      <div className="quantitative-results">
        <div className="chart-controls">
          <div className="chart-toggle-container">
            <button
              className={`chart-toggle-btn ${chartType === 'bar' ? 'active' : ''}`}
              onClick={() => setChartTypes(prev => ({ ...prev, [questionId]: 'bar' }))}
              title="Bar chart"
            >
              Bar
            </button>
            <button
              className={`chart-toggle-btn ${chartType === 'pie' ? 'active' : ''}`}
              onClick={() => setChartTypes(prev => ({ ...prev, [questionId]: 'pie' }))}
              title="Pie chart"
            >
              Pie
            </button>
          </div>
        </div>

        <div className="results-chart-container">
          {chartType === 'bar' ? (
            <Bar data={chartData} options={chartOptions} />
          ) : (
            <Pie data={chartData} options={pieOptions} />
          )}
        </div>

        <div className="results-summary">
          <strong>Total Responses:</strong> {total}
        </div>
        <div className="option-breakdown">
          <h4>Response Options</h4>
          <div className="option-breakdown-list">
            {chartData.labels.map((label, idx) => {
              const value = chartData.datasets[0].data[idx] || 0;
              const percent = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
              return (
                <div key={`${label}-${idx}`} className="option-breakdown-item">
                  <span className="option-label" title={label}>{label}</span>
                  <span className="option-metrics">{value} ({percent}%)</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderQualitativeResults = (questionId, result) => {
    const responses = result.responses || [];

    // Debug logging
    console.log('Qualitative responses for question', questionId, ':', responses);

    // Group by persona
    const grouped = responses.reduce((acc, resp) => {
      const personaId = resp.persona_id;
      if (!acc[personaId]) {
        acc[personaId] = {
          persona_name: resp.persona_name,
          responses: []
        };
      }
      acc[personaId].responses.push(resp);
      return acc;
    }, {});

    return (
      <div className="qualitative-results">
        <div className="qualitative-scroll-region">
          {Object.entries(grouped).map(([personaId, data]) => (
            <div key={personaId} className="persona-responses">
              <h4 className="persona-name">
                {data.persona_name} ({data.responses.length} {data.responses.length === 1 ? 'response' : 'responses'})
              </h4>
              <div className="responses-list">
                {data.responses.map((resp, idx) => {
                  // Extract response text from the response object
                  let responseText;

                  // Qualitative responses are stored as JSON objects with a "response" field
                  if (typeof resp.response === 'object' && resp.response !== null) {
                    // Extract from JSON structure: {"response": "text here"}
                    responseText = resp.response.response || resp.response.text || resp.response.content;

                    // If still no text found, stringify the object
                    if (!responseText) {
                      responseText = JSON.stringify(resp.response);
                    }
                  } else if (typeof resp.response === 'string') {
                    // Handle legacy string responses
                    responseText = resp.response;
                  } else {
                    responseText = String(resp.response || '');
                  }

                  // Show a message if response is empty
                  if (!responseText || (typeof responseText === 'string' && responseText.trim() === '')) {
                    responseText = '[No response provided]';
                  }

                  return (
                    <div key={idx} className="response-item">
                      <span className="response-number">Response {resp.response_number}:</span>
                      <p className="response-text">{responseText}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="results-summary">
          <strong>Total Responses:</strong> {responses.length}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="results-loading">
          <div className="results-loading-content">
            <div className="results-loading-spinner"></div>
            <div className="results-loading-text">Loading results...</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="results-error">
          <p>{error}</p>
          <button onClick={fetchResults}>Retry</button>
        </div>
      </Layout>
    );
  }

  if (!report) {
    return (
      <Layout>
        <div className="results-error">Report not found</div>
      </Layout>
    );
  }

  const isRunning = report.status === 'running';
  const isCompleted = report.status === 'completed';

  return (
    <Layout>
      <div className="survey-results">
        <header className="results-header">
        <div>
          <h1>{report.title}</h1>
          {report.survey_snapshot?.description && (
            <p className="survey-description">{report.survey_snapshot.description}</p>
          )}
          <div className="survey-meta">
            <span className={`status-badge status-${report.status}`}>
              {report.status}
            </span>
            <span className="meta-item">
              Survey: {report.survey_snapshot?.title || 'Unknown'}
            </span>
            {report.completed_at && (
              <span className="completion-date">
                Completed: {new Date(report.completed_at).toLocaleString()}
              </span>
            )}
          </div>
        </div>
        <div className="header-actions">
          {isCompleted && (
            <button className="btn-export" onClick={handleExport}>
              Export CSV
            </button>
          )}
          <button className="btn-back" onClick={() => navigate('/synthetic-audience-workshop')}>
            ← Back
          </button>
        </div>
      </header>

      {isRunning && (
        <div className="progress-section">
          <h3>Report in Progress...</h3>
          <p>Status updates live. Refresh this page to load newly generated responses.</p>
          <div className="progress-indicator">
            <div className="spinner"></div>
            <span>Generating responses...</span>
          </div>
        </div>
      )}

      <div className="results-content-area">
        {availablePersonas.length > 0 && (
          <div className="persona-filter">
            <div className="filter-header">
              <h3>Filter by Personas</h3>
              <div className="filter-actions">
                <button onClick={selectAllPersonas} className="filter-action-btn">
                  Select All
                </button>
                <button onClick={clearAllPersonas} className="filter-action-btn">
                  Clear All
                </button>
              </div>
            </div>
            <div className="persona-chips">
              {availablePersonas.map(persona => (
                <button
                  key={persona.id}
                  className={`persona-chip ${selectedPersonas.includes(persona.id) ? 'active' : ''}`}
                  onClick={() => togglePersona(persona.id)}
                >
                  {persona.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="results-container">
          {report.survey_snapshot?.questions && report.survey_snapshot.questions.length > 0 ? (
            report.survey_snapshot.questions.map((question, idx) => {
              const questionResults = filteredResults[question.id];

              return (
                <div
                  key={question.id}
                  className={`question-results ${question.type === 'qualitative' ? 'question-results--qualitative' : ''}`}
                >
                  <div className="question-header">
                    <h3>Question {idx + 1}</h3>
                    <span className={`question-type-badge type-${question.type}`}>
                      {question.type.replace('_', ' ')}
                    </span>
                  </div>

                  <p className="question-text">{question.text}</p>

                  {/* Attachment Thumbnails */}
                  {question.attachments && question.attachments.length > 0 && (
                    <div className="question-attachments-results">
                      {question.attachments.map((att, attIdx) => (
                        <div
                          key={attIdx}
                          className="attachment-thumb-results"
                          onClick={() => setEnlargedAttachment(att)}
                          title={`Click to enlarge ${att.name}`}
                        >
                          {att.type.startsWith('image/') ? (
                            <img src={getAttachmentUrl(att)} alt={att.name} />
                          ) : (
                            <div className="pdf-thumb-results">
                              <span className="pdf-icon">📄</span>
                              <span className="pdf-name">{att.name}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {questionResults ? (
                    question.type === 'qualitative' ?
                      renderQualitativeResults(question.id, questionResults) :
                      renderQuantitativeResults(question.id, questionResults, question)
                  ) : (
                    <div className="no-responses">
                      No responses yet for this question
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="no-questions">No questions in this report</div>
          )}
        </div>
      </div>
    </div>

    {/* Enlarged Attachment Modal */}
    {enlargedAttachment && (
      <div className="attachment-modal-overlay" onClick={() => setEnlargedAttachment(null)}>
        <div className="attachment-modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close-btn" onClick={() => setEnlargedAttachment(null)}>
            ×
          </button>
          <div className="attachment-modal-body">
            {enlargedAttachment.type.startsWith('image/') ? (
              <img
                src={getAttachmentUrl(enlargedAttachment)}
                alt={enlargedAttachment.name}
                className="enlarged-image"
              />
            ) : (
              <div className="enlarged-pdf">
                <div className="pdf-preview-header">
                  <span className="pdf-icon-large">📄</span>
                  <span className="pdf-name-large">{enlargedAttachment.name}</span>
                </div>
                <p className="pdf-info">PDF files cannot be previewed directly. The AI model can view the content.</p>
              </div>
            )}
          </div>
          <div className="attachment-modal-footer">
            <span className="attachment-name">{enlargedAttachment.name}</span>
          </div>
        </div>
      </div>
    )}
    </Layout>
  );
};

export default SurveyResults;
